import { changeLogOutput, draftDiffOutput, exclusionListOutput, exclusionPath, planCreated, planDraftChangeRejectPath, planItemAlternativesOutput, planItemAlternativesPath, planItemCandidatesPath, planItemCreated, planItemPath, planItemReplacePath, planItemRestorePath, planItemSessionPath, planItemTargetsPath, planItemView, planOutput, planPublishOutput, planRestartWeeksOutput, planSessionCandidatesPath, planSessionCreated, planSessionPath, planView, slotCandidatesOutput, TRAINING_ROUTES, } from "@repo/contract/training";
import { toApiError } from "../../api/api-error";
import { getJson, sendJson, sendNoContent, withQuery, } from "../core/request";
/** `GET /me/plan` — the plan in effect, or `null` for an account with none. */
export function fetchActivePlan(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.plan, { locale: options.locale }), planOutput, init);
}
/**
 * `DELETE /me/plan/suggestion` — "not now" on the standing suggestion (T12).
 * A `204`, whether or not there was one to dismiss, and a second call answers
 * `204` too, so a retry is safe.
 *
 * It does NOT withdraw the proposal: the engine still holds it, and only the
 * app's view of it is stamped (`suggestionDismissedAt`). The effect a caller
 * sees is that the next {@link fetchActivePlan}, on this device and on every
 * other, answers `suggestion: null` — which is why the card that calls this
 * refreshes the plan rather than hiding it locally and forgetting.
 */
export function dismissPlanSuggestion(api) {
    return sendNoContent(api, "DELETE", TRAINING_ROUTES.planSuggestion);
}
/** `GET /me/plan?draft=1` — the draft beside the incumbent, or `null`. */
export function fetchDraftPlan(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.plan, { draft: "1", locale: options.locale }), planOutput, init);
}
/**
 * `POST /me/plan/generate` — the engine proposes a plan, as a DRAFT, and the
 * whole draft `planView` comes back.
 *
 * ── ONLY THE DRAFT REVIEW SCREEN CALLS THIS (`D150`, `T-145`) ───────────────
 * The review screen generates its draft once, from the `protocolId` and
 * `excludeExerciseIds` it is handed. Nothing that NAVIGATES to it may call
 * this first: a caller that generates before navigating has its draft replaced
 * by a second one on the incumbent's protocol — two 200s, a valid deck, the
 * wrong plan, and nothing red. Pass the inputs to the screen; let it call.
 */
export function generatePlan(api, input) {
    const { locale, ...body } = input;
    return sendJson(api, "POST", withQuery(TRAINING_ROUTES.planGenerate, { locale }), body, planView);
}
/**
 * `POST /me/plan/publish` — the draft becomes the plan in effect. A second
 * publish of the same plan answers `changed: false`, not an error.
 *
 * `rejectChangeIds` are the draft's changes refused on the device, applied in
 * the same request (`D182`). The key is sent ONLY when the list is non-empty:
 * the input is strict, so a server older than the field answers 422 to any
 * body that names it, and a draft accepted as proposed must still publish
 * there. `skippedChangeIds` in the answer are the ids the draft no longer
 * contained; a server older than the field never sends it.
 */
export function publishPlan(api, input) {
    const { rejectChangeIds, ...rest } = input;
    const body = rejectChangeIds === undefined || rejectChangeIds.length === 0
        ? rest
        : { ...rest, rejectChangeIds };
    return sendJson(api, "POST", TRAINING_ROUTES.planPublish, body, planPublishOutput);
}
/**
 * `GET /me/plan/draft/diff` — what the account's draft changes against the
 * plan in effect (T32). A 404 `plan.no_draft` when there is none, which
 * arrives as an `ApiError` with that key. It never generates a draft
 * (`D150`, `T-145`).
 */
export function fetchDraftDiff(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.planDraftDiff, { locale: options.locale }), draftDiffOutput, init);
}
/**
 * `POST /me/plan/draft/changes/:changeId/reject` — the draft's slot goes back
 * to the plan in effect's version, and the RECOMPUTED diff comes back. The
 * `changeId` is the diff's, echoed and never built. There is no "accept one":
 * whatever is not rejected is accepted by {@link publishPlan}.
 */
export function rejectDraftChange(api, input) {
    return sendJson(api, "POST", withQuery(planDraftChangeRejectPath(input.changeId), {
        locale: input.locale,
    }), undefined, draftDiffOutput);
}
/** `DELETE /me/plan/draft` — discard the draft (a soft delete). A `204`. */
export function discardDraft(api) {
    return sendNoContent(api, "DELETE", TRAINING_ROUTES.planDraft);
}
/**
 * `POST /me/plan/items` — an exercise into a session, appended at its end.
 * With `prescribe: true` and no target of its own, the server writes the
 * targets the engine would give that exercise in week 1 of the plan's
 * protocol (`D182`); the contract refuses the two together.
 */
export function addPlanItem(api, input) {
    return sendJson(api, "POST", TRAINING_ROUTES.planItems, input, planItemCreated);
}
/** `DELETE /me/plan/items/:id` — a `204`. */
export function removePlanItem(api, itemId) {
    return sendNoContent(api, "DELETE", planItemPath(itemId));
}
/**
 * `PUT /me/plan/items/:id/replace` — another exercise in THIS item's slot, in
 * one call. The answer is the NEW item's `planItemView`: same session, same
 * position, the old item's sets, reps, hold, rest and notes, with
 * `targetLoad` and `reason` null.
 *
 * Use this for "swap for the plan", never `removePlanItem` + `addPlanItem` +
 * `updatePlanSession`: those three have no transaction across them (`T-90`),
 * so a dropped connection leaves an empty slot or the new exercise at the end
 * of the session, with every call answering 2xx. The locale names the joined
 * exercise in the answer, as on the plan read.
 */
export function replacePlanItem(api, input) {
    const { itemId, locale, ...body } = input;
    return sendJson(api, "PUT", withQuery(planItemReplacePath(itemId), { locale }), body, planItemView);
}
/**
 * `POST /me/plan/items/:id/restore` — the undo of a remove, or with
 * `replacing` the undo of a replace (`D182`). The answer is the restored row
 * with the SAME `itemId`: nothing is copied, so its targets, load, reason and
 * notes come back as they were. With `replacing`, the item the replace
 * created is soft-deleted in the same mutation, so the slot is never empty or
 * doubled between two calls (`T-90`). A retry after success answers 200 and
 * writes nothing.
 */
export function restorePlanItem(api, input) {
    const { itemId, locale, ...body } = input;
    return sendJson(api, "POST", withQuery(planItemRestorePath(itemId), { locale }), body, planItemView);
}
/**
 * `POST /me/plan` — a new EMPTY draft, origin manual; the drafts before it
 * are discarded by the server. With {@link publishPlan} after it, the blank
 * plan a person builds by hand (`D197`).
 */
export function createPlan(api) {
    return sendJson(api, "POST", TRAINING_ROUTES.plan, {}, planCreated);
}
/**
 * `POST /me/plan/restart-weeks` — week 1 of the plan in effect starts now
 * (`D198`). The plan is named so a restart from a screen that still showed a
 * replaced plan is refused, 404 `plan.not_found`.
 */
export function restartPlanWeeks(api, planId) {
    return sendJson(api, "POST", TRAINING_ROUTES.planRestartWeeks, { planId }, planRestartWeeksOutput);
}
/**
 * `POST /me/plan/sessions` — a new, empty day at the end of the plan. With
 * the client's `id` the add is replayable (`D197`): a resend answers the day
 * already stored.
 */
export function addPlanSession(api, input) {
    return sendJson(api, "POST", TRAINING_ROUTES.planSessions, input, planSessionCreated);
}
/**
 * `DELETE /me/plan/sessions/:id` — a day AND its lines (`D197`). A `204`.
 * A day no longer on the plan answers 404 `plan.session_not_found`, which a
 * caller resending a removal reads as done: only a removal takes a day off.
 */
export function removePlanSession(api, sessionId) {
    return sendNoContent(api, "DELETE", planSessionPath(sessionId));
}
/**
 * `PUT /me/plan/session-order` — every visible day of the plan, in the order
 * they are to follow (`D210`). 204; the same order sent twice is the same
 * plan, so the outbox may replay it.
 */
export function reorderPlanSessions(api, input) {
    return sendNoContent(api, "PUT", TRAINING_ROUTES.planSessionOrder, input);
}
/**
 * `PUT /me/plan/superset` — lines of one day standing next to each other
 * grouped as a superset, or taken out of theirs (`D246`). 204; a request that
 * finds the lines already as asked writes nothing, so the outbox may replay
 * it.
 */
export function setPlanSuperset(api, input) {
    return sendNoContent(api, "PUT", TRAINING_ROUTES.planSuperset, input);
}
/**
 * `PUT /me/plan/items/:id/targets` — the person's own sets, reps, hold, rest
 * or load for one line (`D197`). The answer is the NEW line in the same slot,
 * under the body's `id`; send only what changed — a body that changes
 * nothing is 422 `plan.retarget_unchanged`.
 */
export function retargetPlanItem(api, input) {
    const { itemId, locale, ...body } = input;
    return sendJson(api, "PUT", withQuery(planItemTargetsPath(itemId), { locale }), body, planItemView);
}
/**
 * `PUT /me/plan/items/:id/session` — the line to another day of the same
 * plan, whole (`D214`). The answer is the NEW line, under the body's `id`.
 */
export function movePlanItem(api, input) {
    const { itemId, locale, ...body } = input;
    return sendJson(api, "PUT", withQuery(planItemSessionPath(itemId), { locale }), body, planItemView);
}
/**
 * `PUT /me/plan/sessions/:id` — label, weekday, and the item order. `itemIds`
 * is EVERY visible item of the session in order, never a move instruction
 * (`planSessionUpdateInput`). A `204`.
 */
export function updatePlanSession(api, input) {
    const { sessionId, ...body } = input;
    return sendNoContent(api, "PUT", planSessionPath(sessionId), body);
}
/**
 * `PUT /me/plan/sessions/:id` carrying ONLY `name` — the rename (`T141`).
 *
 * Separate from {@link updatePlanSession} rather than a second caller of it
 * because the two writes have opposite shapes: that one spreads whatever the
 * caller passed, and a rename must send exactly one key. The body is typed as
 * `PlanSessionUpdateInput` so the contract, not this file, decides what is
 * sendable. A `204`, never a body.
 */
export function renamePlanSession(api, input) {
    const body = { name: input.name };
    return sendNoContent(api, "PUT", planSessionPath(input.sessionId), body);
}
/** `GET /me/plan/items/:id/alternatives` — at most three swaps (`D106`). */
export function fetchPlanItemAlternatives(api, itemId, options = {}, init) {
    return getJson(api, withQuery(planItemAlternativesPath(itemId), { locale: options.locale }), planItemAlternativesOutput, init);
}
/** `GET /me/plan/items/:id/candidates` — one page of the editor's feed. An
 *  empty pool is zero rows plus `exhaustedReasonKey`, never a 404. */
export function fetchPlanItemCandidates(api, itemId, options = {}, init) {
    return getJson(api, withQuery(planItemCandidatesPath(itemId), {
        cursor: options.cursor,
        limit: options.limit,
        locale: options.locale,
    }), slotCandidatesOutput, init);
}
/**
 * `GET /me/plan/sessions/:id/candidates` — one page of exercises to ADD to a
 * session (`D182`): the slot feed's filters and ranking, narrowed to
 * `muscleId`'s targets, minus what the session already shows. An empty pool
 * is zero rows plus `exhaustedReasonKey`, never a 404.
 */
export function fetchSessionCandidates(api, sessionId, options, init) {
    return getJson(api, withQuery(planSessionCandidatesPath(sessionId), {
        cursor: options.cursor,
        limit: options.limit,
        locale: options.locale,
        muscleId: options.muscleId,
    }), slotCandidatesOutput, init);
}
/** `GET /me/exclusions` — the refusal list, bounded, unpaged (`D06`). */
export function fetchExclusions(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.exclusions, { locale: options.locale }), exclusionListOutput, init);
}
/**
 * `POST /me/exclusions` — idempotent: `201` the first time, `200` after,
 * both with NO body, so the status is the whole answer. At the cap,
 * `409 exclusion.limit_reached`.
 *
 * Not `sendNoContent`, which reads nothing of a 2xx: this is the one write
 * whose status carries meaning, and it is read here rather than widening the
 * shared writer for a single route.
 */
export async function addExclusion(api, input) {
    const response = await api.authorizedFetch(TRAINING_ROUTES.exclusions, {
        body: JSON.stringify(input),
        headers: { "content-type": "application/json" },
        method: "POST",
    });
    if (!response.ok) {
        throw await toApiError(response);
    }
    return { created: response.status === 201 };
}
/** `DELETE /me/exclusions/:exerciseId` — `204` whether or not it was there. */
export function removeExclusion(api, exerciseId) {
    return sendNoContent(api, "DELETE", exclusionPath(exerciseId));
}
/**
 * `GET /me/changes` — one page of the account's history, newest first.
 *
 * A plan entry may carry `items` (T35): which exercise went out and which
 * came in, each side named in `locale`. Optional on the wire — a server
 * older than T35 never sends it, and the entry's actor fields are the only
 * attribution a line has.
 */
export function fetchChanges(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.changes, {
        before: options.before,
        limit: options.limit,
        locale: options.locale,
    }), changeLogOutput, init);
}
//# sourceMappingURL=api.js.map